/**
 * Small authoring toolkit over the in-house spreadsheet snapshot protocol.
 *
 * The CFO forecast workbook is large and formula-driven; hand-assembling the raw
 * `SpreadsheetWorkbookSnapshot` (chunk ids, A1 refs, validation/CF/named-range
 * registries) per sheet would be error-prone. `SheetWriter` accumulates cells,
 * formulas, merges, validations, conditional formats, named ranges and charts
 * with a readable API and emits a `SpreadsheetSheetSnapshot`; `WorkbookBuilder`
 * stitches the sheets together with the workbook-level registries and exports
 * through the engine. Everything here is pure and unit-testable.
 */
import {
  buildSheetChunkId,
  buildSheetChunkKey,
  canonicalizeSpreadsheetCellFormatStyle,
  canonicalizeSpreadsheetValidationRule,
  SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  type SpreadsheetCellRecord,
  type SpreadsheetCellValue,
  type SpreadsheetDerivedArtifact,
  type SpreadsheetRangeAddress,
  type SpreadsheetSheetSnapshot,
  type SpreadsheetValidationRule,
  type SpreadsheetWorkbookSnapshot,
} from '../../../protocol/spreadsheetEngineProtocol'

export type CellPrimitive = string | number | boolean | null | undefined

/** Chart series spec (mirrors the engine's native chart synthesiser). */
export type CfoChartSeries = {
  name?: string
  nameRef?: string
  categoriesRef?: string
  valuesRef: string
  type?: 'col' | 'bar' | 'line' | 'area'
  color?: string
  secondaryAxis?: boolean
  smooth?: boolean
  /** Fill opacity 0-100 (translucent band fills). */
  alpha?: number
  /** No fill (invisible base series of a stacked-area band). */
  noFill?: boolean
}

export type CfoChartSpec = {
  title?: string
  series: CfoChartSeries[]
  legend?: 'r' | 'l' | 't' | 'b' | 'none'
  catTitle?: string
  valTitle?: string
  valTitle2?: string
  grouping?: 'standard' | 'stacked' | 'percentStacked'
}

// ---------------------------------------------------------------------------
// A1 reference helpers (0-based row/col in, 1-based A1 out)
// ---------------------------------------------------------------------------

export function colToLetter(col: number): string {
  let n = Math.max(0, Math.floor(col))
  let out = ''
  for (;;) {
    out = String.fromCharCode(65 + (n % 26)) + out
    n = Math.floor(n / 26) - 1
    if (n < 0) break
  }
  return out
}

/** A1 reference within a sheet. Absolute (`$B$3`) by default. */
export function cellRef(row: number, col: number, absolute = true): string {
  const colPart = `${absolute ? '$' : ''}${colToLetter(col)}`
  const rowPart = `${absolute ? '$' : ''}${row + 1}`
  return `${colPart}${rowPart}`
}

/** Always single-quote the sheet name (matches the engine's own formula quoting). */
export function quoteSheet(name: string): string {
  return `'${name.replace(/'/g, "''")}'`
}

/** Cross-sheet A1 reference: `'Sheet Name'!$B$3`. */
export function crossRef(sheetName: string, row: number, col: number, absolute = true): string {
  return `${quoteSheet(sheetName)}!${cellRef(row, col, absolute)}`
}

/** Cross-sheet A1 range: `'Sheet Name'!$B$3:$D$9`. */
export function crossRangeRef(sheetName: string, r1: number, c1: number, r2: number, c2: number, absolute = true): string {
  return `${quoteSheet(sheetName)}!${cellRef(r1, c1, absolute)}:${cellRef(r2, c2, absolute)}`
}

function toCellValue(value: CellPrimitive): SpreadsheetCellValue {
  if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'number', value }
  if (typeof value === 'boolean') return { kind: 'boolean', value }
  if (typeof value === 'string') return { kind: 'text', value }
  return { kind: 'blank' }
}

function toRawInput(value: CellPrimitive): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'string') return value
  return ''
}

export type PutCellArgs = {
  /** Literal value (text/number/boolean). Ignored when `formula` is set. */
  value?: CellPrimitive
  /** Formula text without the leading `=` (added automatically), e.g. `SUM(A1:A3)`. */
  formula?: string
  /** Cached numeric/text value for a formula so the file is correct before recalc. */
  cached?: CellPrimitive
  styleId?: string | null
  validationId?: string | null
  comment?: string | null
  /** Per-cell protection override (e.g. `{ locked: false }` to leave an input editable). */
  protection?: Record<string, unknown> | null
}

// ---------------------------------------------------------------------------
// Conditional format / named range collection (workbook-scoped, surfaced by SheetWriter)
// ---------------------------------------------------------------------------

export type SheetConditionalFormat = {
  id: string
  kind: string
  ranges: SpreadsheetRangeAddress[]
  priority?: number
  operator?: string
  formula1?: string | null
  formula2?: string | null
  /** Differential format (fill/font) applied when the rule matches (expression/cellIs). */
  format?: Record<string, unknown>
  stop_if_true?: boolean
  /** Native data-bar spec (kind 'dataBar'). */
  dataBar?: { color?: string; min?: Record<string, unknown>; max?: Record<string, unknown>; showValue?: boolean }
  /** Native colour-scale spec (kind 'colorScale'). */
  colorScale?: { stops: Array<{ cfvo: Record<string, unknown>; color: string }> }
  /** Native icon-set spec (kind 'iconSet'). */
  iconSet?: { iconSetType?: string; cfvos?: Array<Record<string, unknown>>; reverse?: boolean; showValue?: boolean }
}

export class SheetWriter {
  readonly sheetId: string
  readonly name: string
  private cells = new Map<string, SpreadsheetCellRecord>()
  private merges: SpreadsheetRangeAddress[] = []
  private colMeta: Record<string, Record<string, unknown>> = {}
  private rowMeta: Record<string, Record<string, unknown>> = {}
  private frozen: { rows: number; cols: number } | null = null
  private showGridLines = false
  private tabSelected = false
  private autoFilterRange: SpreadsheetRangeAddress | null = null
  private protection: SpreadsheetSheetSnapshot['sheet_protection'] | null = null
  private printSettings: SpreadsheetSheetSnapshot['print_settings'] | null = null
  private sheetFormat: SpreadsheetSheetSnapshot['sheet_format_properties'] | null = null
  private hidden = false
  private maxRow = 0
  private maxCol = 0
  /** Surfaced to the WorkbookBuilder. */
  readonly namedRanges: Record<string, SpreadsheetRangeAddress> = {}
  readonly conditionalFormats: SheetConditionalFormat[] = []
  readonly charts: Array<{ id: string; placement: SpreadsheetRangeAddress; spec: CfoChartSpec }> = []

  constructor(sheetId: string, name: string) {
    this.sheetId = sheetId
    this.name = name
  }

  private key(row: number, col: number): string {
    return `${row}:${col}`
  }

  put(row: number, col: number, args: PutCellArgs): this {
    this.maxRow = Math.max(this.maxRow, row)
    this.maxCol = Math.max(this.maxCol, col)
    const isFormula = typeof args.formula === 'string' && args.formula.trim().length > 0
    const rawInput = isFormula ? `=${args.formula!.trim().replace(/^=/, '')}` : toRawInput(args.value)
    const computed = isFormula
      ? (args.cached === undefined ? null : toCellValue(args.cached))
      : toCellValue(args.value)
    const record: SpreadsheetCellRecord = {
      raw_input: rawInput,
      computed_value: computed,
      style_id: args.styleId ?? null,
      validation_id: args.validationId ?? null,
      comment: args.comment ?? null,
      protection: args.protection ?? null,
      revision: 1,
    }
    this.cells.set(this.key(row, col), record)
    return this
  }

  text(row: number, col: number, value: CellPrimitive, styleId?: string | null): this {
    return this.put(row, col, { value, styleId })
  }

  number(row: number, col: number, value: number | null, styleId?: string | null): this {
    return this.put(row, col, { value, styleId })
  }

  formula(row: number, col: number, formula: string, cached: CellPrimitive, styleId?: string | null): this {
    return this.put(row, col, { formula, cached, styleId })
  }

  merge(r1: number, c1: number, r2: number, c2: number): this {
    this.merges.push({ sheet_id: this.sheetId, start_row: r1, start_col: c1, end_row: r2, end_col: c2 })
    this.maxRow = Math.max(this.maxRow, r2)
    this.maxCol = Math.max(this.maxCol, c2)
    return this
  }

  colWidth(col: number, width: number): this {
    // `width` is in Excel character-width units; the engine emits widths from
    // `width_px` (pixels, ~7px per char unit). Storing the wrong field/unit left
    // every <col> with no width — which Excel renders as zero-width/hidden
    // columns (content invisible), though Google Sheets falls back to a default.
    this.colMeta[String(col)] = { ...(this.colMeta[String(col)] ?? {}), width_px: Math.round(width * 7) }
    return this
  }

  colWidths(startCol: number, widths: number[]): this {
    widths.forEach((w, i) => this.colWidth(startCol + i, w))
    return this
  }

  rowHeight(row: number, height: number): this {
    // `height` is in points; the engine emits ht from `height_px` (pixels × 0.75).
    this.rowMeta[String(row)] = { ...(this.rowMeta[String(row)] ?? {}), height_px: Math.round(height / 0.75) }
    return this
  }

  /** Excel column outline grouping (the +/- collapse controls). `hidden` collapses on open. */
  colOutline(col: number, level: number, opts?: { collapsed?: boolean; hidden?: boolean }): this {
    this.colMeta[String(col)] = {
      ...(this.colMeta[String(col)] ?? {}),
      outline_level: Math.max(0, Math.floor(level)),
      ...(opts?.collapsed ? { collapsed: true } : {}),
      ...(opts?.hidden ? { hidden: true } : {}),
    }
    return this
  }

  /** Excel row outline grouping (collapse account detail under its totals). */
  rowOutline(row: number, level: number, opts?: { collapsed?: boolean; hidden?: boolean }): this {
    this.rowMeta[String(row)] = {
      ...(this.rowMeta[String(row)] ?? {}),
      outline_level: Math.max(0, Math.floor(level)),
      ...(opts?.collapsed ? { collapsed: true } : {}),
      ...(opts?.hidden ? { hidden: true } : {}),
    }
    return this
  }

  /** Sheet-level outline properties (summary placement + show the +/- symbols). */
  setSheetOutline(props: { summaryRight?: boolean; summaryBelow?: boolean; showSymbols?: boolean }): this {
    this.sheetFormat = {
      ...(this.sheetFormat ?? {}),
      ...(props.summaryRight != null ? { outline_summary_right: props.summaryRight } : {}),
      ...(props.summaryBelow != null ? { outline_summary_below: props.summaryBelow } : {}),
      ...(props.showSymbols != null ? { show_outline_symbols: props.showSymbols } : {}),
    }
    return this
  }

  freeze(rows: number, cols: number): this {
    this.frozen = { rows, cols }
    return this
  }

  select(): this {
    this.tabSelected = true
    return this
  }

  hide(): this {
    this.hidden = true
    return this
  }

  protect(options: NonNullable<SpreadsheetSheetSnapshot['sheet_protection']>): this {
    this.protection = { enabled: true, ...options }
    return this
  }

  setAutoFilter(r1: number, c1: number, r2: number, c2: number): this {
    this.autoFilterRange = { sheet_id: this.sheetId, start_row: r1, start_col: c1, end_row: r2, end_col: c2 }
    return this
  }

  setPrintSettings(settings: NonNullable<SpreadsheetSheetSnapshot['print_settings']>): this {
    this.printSettings = settings
    return this
  }

  named(name: string, row: number, col: number, endRow?: number, endCol?: number): this {
    this.namedRanges[name] = {
      sheet_id: this.sheetId,
      start_row: row,
      start_col: col,
      end_row: endRow ?? row,
      end_col: endCol ?? col,
    }
    return this
  }

  range(r1: number, c1: number, r2: number, c2: number): SpreadsheetRangeAddress {
    return { sheet_id: this.sheetId, start_row: r1, start_col: c1, end_row: r2, end_col: c2 }
  }

  /** Absolute cross-sheet A1 range string to this sheet's own cells (for chart series refs). */
  ref(r1: number, c1: number, r2: number, c2: number): string {
    return crossRangeRef(this.name, r1, c1, r2, c2)
  }

  /** Place a native chart anchored to the given cell box on this sheet. */
  chart(id: string, placement: { r1: number; c1: number; r2: number; c2: number }, spec: CfoChartSpec): this {
    this.maxRow = Math.max(this.maxRow, placement.r2)
    this.maxCol = Math.max(this.maxCol, placement.c2)
    this.charts.push({ id, placement: this.range(placement.r1, placement.c1, placement.r2, placement.c2), spec })
    return this
  }

  addConditionalFormat(rule: Omit<SheetConditionalFormat, 'ranges'> & { ranges: SpreadsheetRangeAddress[] }): this {
    this.conditionalFormats.push(rule)
    return this
  }

  build(): SpreadsheetSheetSnapshot {
    const chunks: Record<string, Record<string, SpreadsheetCellRecord>> = {}
    for (const [key, record] of this.cells) {
      const [rowStr, colStr] = key.split(':')
      const row = Number(rowStr)
      const col = Number(colStr)
      const chunkId = buildSheetChunkId(buildSheetChunkKey(this.sheetId, row, col))
      chunks[chunkId] ??= {}
      chunks[chunkId][key] = record
    }
    const sheet: SpreadsheetSheetSnapshot = {
      sheet_id: this.sheetId,
      name: this.name,
      row_count: Math.max(1, this.maxRow + 1),
      col_count: Math.max(1, this.maxCol + 1),
      chunks,
      column_metadata: this.colMeta,
      row_metadata: this.rowMeta,
      hidden: this.hidden,
      sheet_views: [{ show_grid_lines: this.showGridLines, tab_selected: this.tabSelected }],
    }
    if (this.frozen) sheet.frozen_panes = this.frozen
    if (this.autoFilterRange) sheet.auto_filter = { range: this.autoFilterRange }
    if (this.protection) sheet.sheet_protection = this.protection
    if (this.printSettings) sheet.print_settings = this.printSettings
    if (this.sheetFormat) sheet.sheet_format_properties = this.sheetFormat
    if (this.merges.length > 0) {
      sheet.merge_ranges = {}
      this.merges.forEach((m, i) => { sheet.merge_ranges![`merge_${i}`] = m })
    }
    return sheet
  }
}

export type WorkbookBuilderOptions = {
  workbookId: string
  title: string
  company?: string | null
  creator?: string
  created?: string
  styles?: Record<string, Record<string, unknown>>
}

export class WorkbookBuilder {
  private sheets: SheetWriter[] = []
  private validationRules: Record<string, SpreadsheetValidationRule> = {}
  private styles: Record<string, Record<string, unknown>>
  private opts: WorkbookBuilderOptions
  private activeTab = 0

  constructor(opts: WorkbookBuilderOptions) {
    this.opts = opts
    this.styles = { ...(opts.styles ?? {}) }
  }

  mergeStyles(styles: Record<string, Record<string, unknown>>): this {
    Object.assign(this.styles, styles)
    return this
  }

  addSheet(writer: SheetWriter): SheetWriter {
    this.sheets.push(writer)
    return writer
  }

  setActiveSheet(writer: SheetWriter): this {
    const index = this.sheets.indexOf(writer)
    if (index >= 0) this.activeTab = index
    return this
  }

  /** Register (deduped) a validation rule and return its id for use as `validationId`. */
  addValidation(rule: Omit<SpreadsheetValidationRule, 'id'> & { id?: string | null }): string {
    const canonical = canonicalizeSpreadsheetValidationRule(rule)
    this.validationRules[canonical.id] = canonical
    return canonical.id
  }

  build(): SpreadsheetWorkbookSnapshot {
    const namedRanges: Record<string, SpreadsheetRangeAddress> = {}
    const conditionalFormats: Record<string, Record<string, unknown>> = {}
    const derivedArtifacts: Record<string, SpreadsheetDerivedArtifact> = {}
    const builtSheets = this.sheets.map((writer) => {
      Object.assign(namedRanges, writer.namedRanges)
      for (const cf of writer.conditionalFormats) {
        conditionalFormats[cf.id] = {
          kind: cf.kind,
          ranges: cf.ranges,
          priority: cf.priority ?? 1,
          ...(cf.operator ? { operator: cf.operator } : {}),
          ...(cf.formula1 != null ? { formula1: cf.formula1 } : {}),
          ...(cf.formula2 != null ? { formula2: cf.formula2 } : {}),
          // Canonicalise friendly keys (backgroundColor/textColor/bold) into the
          // {font, fill, ...} shape the engine's dxf builder reads — otherwise the
          // dxf comes out empty and the expression cfRule ships with no required
          // dxfId, which Excel flags as unreadable.
          ...(cf.format ? { format: canonicalizeSpreadsheetCellFormatStyle(cf.format) } : {}),
          ...(cf.stop_if_true ? { stop_if_true: true } : {}),
          ...(cf.dataBar ? { dataBar: cf.dataBar } : {}),
          ...(cf.colorScale ? { colorScale: cf.colorScale } : {}),
          ...(cf.iconSet ? { iconSet: cf.iconSet } : {}),
        }
      }
      for (const chart of writer.charts) {
        derivedArtifacts[chart.id] = {
          id: chart.id,
          sheet_id: writer.sheetId,
          kind: 'chart',
          source_range: chart.placement,
          placement: chart.placement,
          metadata: { chart_spec: chart.spec },
        }
      }
      return writer.build()
    })
    const nowIso = this.opts.created ?? '1970-01-01T00:00:00.000Z'
    return {
      protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
      workbook_id: this.opts.workbookId,
      workbook_revision: 1,
      workbook_properties: { date_1904: false },
      calculation_settings: { calc_mode: 'auto', full_calc_on_load: true, force_full_calc: true },
      workbook_views: [{ active_tab: this.activeTab }],
      document_properties: {
        title: this.opts.title,
        creator: this.opts.creator ?? 'FuseGrid CFO Core',
        company: this.opts.company ?? undefined,
        created: nowIso,
        modified: nowIso,
        application: 'FuseGrid CFO Core',
      },
      sheets: builtSheets,
      sheet_order: builtSheets.map((s) => s.sheet_id),
      named_ranges: namedRanges,
      validation_rules: this.validationRules,
      styles: this.styles,
      conditional_formats: conditionalFormats,
      derived_artifacts: derivedArtifacts,
      external_sources: {},
    }
  }
}
